"use client";

import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  ApiError,
  api,
  clearTokens,
  loadTokens,
  type MeProfile,
} from "@/lib/api";
import { loginUrl } from "@/lib/loginUrl";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteSidebar } from "@/components/SiteSidebar";
import { Skeleton } from "@/components/Skeleton";

/**
 * EPIC-021 STORY-373 phase 2 — LGPD self-service surface.
 *
 * Two flows on this page:
 *
 *   1. **Right to portability** — "Download my data" button calls
 *      `/v1/me/data-export` and saves the JSON bundle as a file.
 *      No deletion side-effect; user can run this any time.
 *
 *   2. **Right to erasure** — `/v1/me/account/delete-request` starts
 *      the 30-day grace period. The backend revokes all active
 *      sessions inside that tx (per STORY-373 backend invariant), so
 *      after submission the user is signed out client-side too.
 *      Re-login during the grace period reaches /settings/account
 *      again, where the cancel-button shows. After the grace ends,
 *      a future cron sweep (filed but not built) anonymizes the
 *      record per STORY-373's spec.
 *
 * Email notifications for the lifecycle events ("deletion requested"
 * / "deletion will execute in N days" / "deletion executed") wire up
 * via STORY-815 once it merges. Until then the LogMailAdapter records
 * the schedule in pod logs (visible to admin during closed beta).
 */
export default function AccountSettingsPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const [authChecked, setAuthChecked] = useState(false);
  const [profile, setProfile] = useState<MeProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Delete-request form state.
  const [showDeleteForm, setShowDeleteForm] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");

  useEffect(() => {
    if (!loadTokens()) {
      router.replace(loginUrl());
      return;
    }
    void api
      .me()
      .then((p) => {
        setProfile(p);
        setAuthChecked(true);
      })
      .catch(() => router.replace(loginUrl()));
  }, [router]);

  const downloadData = async () => {
    setBusy(true);
    setError(null);
    setFlash(null);
    try {
      const bundle = await api.exportMyData();
      const blob = new Blob([JSON.stringify(bundle, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `granbay-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setFlash(t("accountSettings.exportSuccess"));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("accountSettings.exportFailed"),
      );
    } finally {
      setBusy(false);
    }
  };

  const submitDeletion = async () => {
    if (deleteReason.trim().length < 30) {
      setError(t("accountSettings.reasonTooShort"));
      return;
    }
    if (!window.confirm(t("accountSettings.confirmRequest"))) {
      return;
    }
    setBusy(true);
    setError(null);
    setFlash(null);
    try {
      const result = await api.requestAccountDeletion(deleteReason);
      setFlash(
        t("accountSettings.deletionRequestedTemplate", {
          when: new Date(result.graceEndsAt).toLocaleString(),
        }),
      );
      // Backend already revoked sessions; clear local tokens too and
      // bounce to login. The user can sign back in during the grace
      // window to reach /settings/account and cancel.
      setTimeout(() => {
        clearTokens();
        router.replace("/login?reason=deletion_requested");
      }, 2500);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError(
          err instanceof Error ? err.message : t("accountSettings.requestFailed"),
        );
      }
    } finally {
      setBusy(false);
    }
  };

  const cancelDeletion = async () => {
    if (!window.confirm(t("accountSettings.confirmCancel"))) return;
    setBusy(true);
    setError(null);
    setFlash(null);
    try {
      await api.cancelAccountDeletion();
      const fresh = await api.me();
      setProfile(fresh);
      setFlash(t("accountSettings.cancelSuccess"));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("accountSettings.cancelFailed"),
      );
    } finally {
      setBusy(false);
    }
  };

  if (!authChecked) {
    return (
      <>
        <SiteHeader />
        <main className="mx-auto max-w-[800px] p-8">
          <div
            role="status"
            aria-label={t("accountSettings.loadingAria")}
            className="space-y-3"
          >
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-20 w-full rounded-md" />
            <Skeleton className="h-20 w-full rounded-md" />
            <Skeleton className="h-9 w-40 rounded-md" />
          </div>
        </main>
      </>
    );
  }

  // Shape of MeProfile — server may or may not surface
  // delete_requested_at depending on /v1/me's response. The backend
  // STORY-373 service stamps it on the row; if the GET endpoint
  // doesn't return it, fall back to "unknown" by treating any
  // present-truthy field as a pending request.
  const pending =
    (profile as unknown as { delete_requested_at?: string | null })
      ?.delete_requested_at ?? null;

  return (
    <>
      <SiteHeader />
      <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-5 px-5 py-4 md:grid-cols-[200px_1fr]">
        <SiteSidebar />
        <main className="min-w-0 max-w-2xl">
          <h1 className="mb-2 text-[22px] font-bold tracking-tight">
            {t("accountSettings.title")}
          </h1>
          <p className="mb-5 text-[13px] text-[var(--color-text3)]">
            {t("accountSettings.subtitle")}
          </p>

          {error && (
            <p
              role="alert"
              className="mb-4 text-[12px]"
              style={{ color: "var(--color-no)" }}
            >
              {error}
            </p>
          )}
          {flash && (
            <p
              role="status"
              className="mb-4 text-[12px]"
              style={{ color: "#7ee787" }}
            >
              {flash}
            </p>
          )}

          {/* Pending-deletion banner (if any) */}
          {pending && (
            <section
              className="mb-6 rounded-md border px-3 py-3 text-[12px]"
              style={{
                borderColor: "color-mix(in srgb, var(--color-status-negative) 40%, transparent)",
                background: "color-mix(in srgb, var(--color-status-negative) 8%, transparent)",
              }}
            >
              <p
                className="flex items-center gap-1.5 font-semibold"
                style={{ color: "#fda4af" }}
              >
                <AlertTriangle size={14} strokeWidth={1.75} aria-hidden="true" />
                {t("accountSettings.pendingTitle")}
              </p>
              <p className="mt-1 text-[var(--color-text2)]">
                {t("accountSettings.pendingBody", {
                  when: new Date(pending).toLocaleString(),
                })}
              </p>
              <button
                type="button"
                onClick={cancelDeletion}
                disabled={busy}
                className="mt-3 min-h-[40px] rounded border border-[var(--color-brand)] px-3 py-1.5 text-[12px] font-semibold disabled:opacity-50 md:min-h-0"
                style={{ color: "var(--color-brand)" }}
              >
                {busy
                  ? t("accountSettings.cancelling")
                  : t("accountSettings.cancelDeletion")}
              </button>
            </section>
          )}

          {/* Right to portability */}
          <section className="mb-6 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-4">
            <h2 className="mb-2 text-[14px] font-bold">
              {t("accountSettings.downloadTitle")}
            </h2>
            <p className="mb-3 text-[12px] text-[var(--color-text3)]">
              {t("accountSettings.downloadBody")}
            </p>
            <button
              type="button"
              onClick={downloadData}
              disabled={busy}
              className="min-h-[40px] rounded border border-[var(--color-border)] px-3 py-1.5 text-[12px] disabled:opacity-50 md:min-h-0"
            >
              {busy
                ? t("accountSettings.downloadWorking")
                : t("accountSettings.downloadCta")}
            </button>
          </section>

          {/* Right to erasure */}
          {!pending && (
            <section className="mb-6 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-4">
              <h2
                className="mb-2 text-[14px] font-bold"
                style={{ color: "#fda4af" }}
              >
                {t("accountSettings.deleteTitle")}
              </h2>
              <p className="mb-3 text-[12px] text-[var(--color-text3)]">
                {t("accountSettings.deleteBody1")}
              </p>
              <p className="mb-3 text-[12px] text-[var(--color-text3)]">
                {t("accountSettings.deleteBody2")}
              </p>
              {!showDeleteForm ? (
                <button
                  type="button"
                  onClick={() => setShowDeleteForm(true)}
                  disabled={busy}
                  className="min-h-[40px] rounded border px-3 py-1.5 text-[12px] disabled:opacity-50 md:min-h-0"
                  style={{
                    borderColor:
                      "color-mix(in srgb, var(--color-status-negative) 50%, transparent)",
                    color: "#fda4af",
                  }}
                >
                  {t("accountSettings.requestDeletion")}
                </button>
              ) : (
                <>
                  <label className="mb-3 block text-[12px]">
                    <span className="mb-1 block text-[10px] uppercase tracking-[0.4px] text-[var(--color-text3)]">
                      {t("accountSettings.reasonLabel", {
                        count: deleteReason.length,
                      })}
                    </span>
                    <textarea
                      value={deleteReason}
                      onChange={(e) => setDeleteReason(e.target.value)}
                      maxLength={500}
                      rows={3}
                      placeholder={t("accountSettings.reasonPlaceholder")}
                      className="w-full rounded border border-[var(--color-border)] bg-[var(--color-bg2)] px-2 py-1.5 text-[12px]"
                      disabled={busy}
                    />
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={submitDeletion}
                      disabled={busy || deleteReason.trim().length < 30}
                      className="min-h-[40px] rounded border px-3 py-1.5 text-[12px] font-semibold disabled:opacity-50 md:min-h-0"
                      style={{
                        borderColor:
                          "color-mix(in srgb, var(--color-status-negative) 70%, transparent)",
                        color: "#fda4af",
                      }}
                    >
                      {busy
                        ? t("accountSettings.deleteWorking")
                        : t("accountSettings.deleteConfirm")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowDeleteForm(false);
                        setDeleteReason("");
                      }}
                      disabled={busy}
                      className="min-h-[40px] rounded border border-[var(--color-border)] px-3 py-1.5 text-[12px] text-[var(--color-text3)] md:min-h-0"
                    >
                      {t("accountSettings.back")}
                    </button>
                  </div>
                </>
              )}
            </section>
          )}
        </main>
      </div>
    </>
  );
}
